// src/pages/BlogDetailPage.tsx
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Calendar,
  User,
  Clock,
  ArrowLeft,
  Heart,
  Share2,
  Bookmark,
  Eye,
  MessageCircle,
  Facebook,
  Twitter,
  Linkedin,
  Link2,
  ChevronRight,
  ChevronLeft,
  Tag,
  X,
} from "lucide-react";
import gsap from "gsap";

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  image: string;
  category: string;
  date: string;
  readTime: number;
  author: string;
  authorAvatar: string;
  featured?: boolean;
  tags: string[];
  views: number;
  likes: number;
  comments: number;
}

const BlogDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);

  // Mock blog posts data
  const allPosts: BlogPost[] = [
    {
      id: 1,
      title: "The Future of Online Education: Trends to Watch in 2024",
      excerpt: "Discover the emerging technologies and methodologies shaping how we learn online.",
      content: `
        <p>The landscape of online education is evolving rapidly. With advancements in artificial intelligence, virtual reality, and personalized learning algorithms, students now have access to unprecedented educational opportunities.</p>
        
        <h2>AI-Powered Personalized Learning</h2>
        <p>Artificial intelligence is revolutionizing how educational content is delivered. AI-powered tutoring systems can adapt to individual learning styles, providing customized feedback and guidance. These systems analyze student performance in real-time, identifying areas where learners struggle and offering targeted resources to help them overcome challenges.</p>
        
        <h2>Immersive VR Experiences</h2>
        <p>Virtual reality environments allow students to immerse themselves in historical events, scientific phenomena, or complex engineering simulations. Imagine walking through ancient Rome, exploring the human bloodstream, or practicing surgical procedures in a risk-free environment. VR is making this possible.</p>
        
        <h2>Micro-credentials and Skills-Based Learning</h2>
        <p>Employers are increasingly valuing skills over degrees. This shift has led to the rise of micro-credentials, digital badges, and skills-based certifications that demonstrate specific competencies. Online platforms are responding by offering stackable credentials that build toward comprehensive skill sets.</p>
        
        <h2>The Rise of Community-Based Learning</h2>
        <p>Learning is inherently social. Online education platforms are incorporating more community features, including peer-to-peer support, study groups, and collaborative projects. These features help combat isolation and create meaningful learning communities.</p>
        
        <h2>What This Means for Learners</h2>
        <p>For learners, these trends mean more flexibility, personalization, and opportunities to acquire in-demand skills. Whether you're starting a new career or advancing in your current field, online education is becoming more effective and accessible than ever before.</p>
      `,
      image: "/blog-1.jpg",
      category: "Education",
      date: "Jan 15, 2024",
      readTime: 8,
      author: "Dr. Sarah Johnson",
      authorAvatar: "https://randomuser.me/api/portraits/women/68.jpg",
      featured: true,
      tags: ["Education", "Technology", "Future", "AI"],
      views: 1245,
      likes: 342,
      comments: 56,
    },
    {
      id: 2,
      title: "10 Essential Skills Every Designer Needs in 2024",
      excerpt: "Stay ahead of the curve with these must-have design skills.",
      content: "<p>Detailed content for this article would go here...</p>",
      image: "/blog-2.jpg",
      category: "Design",
      date: "Jan 12, 2024",
      readTime: 6,
      author: "Emily Chen",
      authorAvatar: "https://randomuser.me/api/portraits/women/44.jpg",
      tags: ["Design", "UI/UX", "Career", "Skills"],
      views: 892,
      likes: 234,
      comments: 45,
    },
    {
      id: 3,
      title: "How to Build a Portfolio That Gets You Hired",
      excerpt: "Tips and tricks for creating a standout portfolio.",
      content: "<p>Detailed content for this article would go here...</p>",
      image: "/blog-3.jpg",
      category: "Career",
      date: "Jan 10, 2024",
      readTime: 5,
      author: "Michael Rodriguez",
      authorAvatar: "https://randomuser.me/api/portraits/men/32.jpg",
      tags: ["Career", "Portfolio", "Job Search"],
      views: 1567,
      likes: 567,
      comments: 89,
    },
    {
      id: 4,
      title: "The Complete Guide to Learning Python",
      excerpt: "Everything you need to know to start your Python journey.",
      content: "<p>Detailed content for this article would go here...</p>",
      image: "/blog-4.jpg",
      category: "Development",
      date: "Jan 8, 2024",
      readTime: 10,
      author: "David Park",
      authorAvatar: "https://randomuser.me/api/portraits/men/45.jpg",
      tags: ["Programming", "Python", "Development"],
      views: 2341,
      likes: 789,
      comments: 123,
    },
    {
      id: 5,
      title: "Marketing Strategies for Freelancers",
      excerpt: "Grow your freelance business with these proven strategies.",
      content: "<p>Detailed content for this article would go here...</p>",
      image: "/blog-5.jpg",
      category: "Marketing",
      date: "Jan 5, 2024",
      readTime: 7,
      author: "Lisa Wong",
      authorAvatar: "https://randomuser.me/api/portraits/women/52.jpg",
      tags: ["Marketing", "Freelance", "Business"],
      views: 678,
      likes: 234,
      comments: 45,
    },
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const foundPost = allPosts.find(p => p.id === Number(id));
      if (foundPost) {
        setPost(foundPost);
        // Get related posts (same category, different id)
        const related = allPosts.filter(p => p.category === foundPost.category && p.id !== foundPost.id).slice(0, 3);
        setRelatedPosts(related);
      }
      setLoading(false);
    }, 500);
  }, [id]);

  useEffect(() => {
    if (post) {
      gsap.fromTo(".post-content", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, ease: "expo.out" });
      gsap.fromTo(".sidebar-content", { opacity: 0, x: 20 }, { opacity: 1, x: 0, duration: 0.6, delay: 0.2, ease: "expo.out" });
    }
  }, [post]);

  const handleLike = () => setLiked(!liked);
  const handleBookmark = () => setBookmarked(!bookmarked);

  const sharePost = (platform: string) => {
    const url = window.location.href;
    const text = post?.title;
    
    switch(platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text || '')}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(text || '')}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
        break;
    }
    setShowShareMenu(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-bold text-black mb-2">Article Not Found</h2>
          <p className="text-sm text-gray-500 mb-4">The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="text-primary hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <main className="relative min-h-screen pt-20 pb-16 bg-white">
      <div className="section-container">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary transition mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="post-content">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                  <span className="px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                    {post.category}
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {post.date}
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {post.readTime} min read
                  </span>
                </div>
                <h1 className="text-2xl lg:text-3xl font-bold text-black mb-4">
                  {post.title}
                </h1>
                <div className="flex items-center gap-3">
                  <img
                    src={post.authorAvatar}
                    alt={post.author}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-medium text-black">{post.author}</p>
                    <p className="text-xs text-gray-500">Author</p>
                  </div>
                </div>
              </div>

              {/* Featured Image */}
              <div className="relative h-64 lg:h-96 rounded-xl overflow-hidden mb-6">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Article Content */}
              <div 
                className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-gray-100">
                {post.tags.map(tag => (
                  <span key={tag} className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-full flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>

              {/* Engagement Actions */}
              <div className="flex items-center gap-4 mt-6 pt-6 border-t border-gray-100">
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition ${liked ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  <Heart className={`w-4 h-4 ${liked ? 'fill-red-500' : ''}`} />
                  {post.likes + (liked ? 1 : 0)}
                </button>
                <button
                  onClick={handleBookmark}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition ${bookmarked ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  <Bookmark className={`w-4 h-4 ${bookmarked ? 'fill-primary' : ''}`} />
                  Bookmark
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg text-gray-600 hover:bg-gray-200 transition"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                  {showShareMenu && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10">
                      <button onClick={() => sharePost('facebook')} className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left flex items-center gap-2">
                        <Facebook className="w-4 h-4 text-blue-600" /> Facebook
                      </button>
                      <button onClick={() => sharePost('twitter')} className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left flex items-center gap-2">
                        <Twitter className="w-4 h-4 text-sky-500" /> Twitter
                      </button>
                      <button onClick={() => sharePost('linkedin')} className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left flex items-center gap-2">
                        <Linkedin className="w-4 h-4 text-blue-700" /> LinkedIn
                      </button>
                      <button onClick={() => sharePost('copy')} className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-gray-500" /> Copy Link
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Comments Section */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-bold text-black mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-primary" />
                  Comments ({post.comments})
                </h3>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0" />
                    <div className="flex-1">
                      <textarea
                        placeholder="Add a comment..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-primary resize-none text-sm"
                      />
                      <button className="mt-2 px-4 py-1.5 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition">
                        Post Comment
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="sidebar-content">
            {/* Author Card */}
            <div className="bg-gray-50 rounded-xl p-5 mb-6">
              <h3 className="text-sm font-bold text-black mb-3">About the Author</h3>
              <div className="flex items-center gap-3 mb-3">
                <img
                  src={post.authorAvatar}
                  alt={post.author}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-semibold text-black">{post.author}</p>
                  <p className="text-xs text-gray-500">Contributing Writer</p>
                </div>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                {post.author} is an expert in {post.category.toLowerCase()} with over 10 years of experience in the field.
              </p>
            </div>

            {/* Stats Card */}
            <div className="bg-gray-50 rounded-xl p-5 mb-6">
              <h3 className="text-sm font-bold text-black mb-3">Article Stats</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 flex items-center gap-2">
                    <Eye className="w-4 h-4" /> Views
                  </span>
                  <span className="font-semibold text-black">{post.views.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 flex items-center gap-2">
                    <Heart className="w-4 h-4" /> Likes
                  </span>
                  <span className="font-semibold text-black">{post.likes.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" /> Comments
                  </span>
                  <span className="font-semibold text-black">{post.comments}</span>
                </div>
              </div>
            </div>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <div className="bg-gray-50 rounded-xl p-5">
                <h3 className="text-sm font-bold text-black mb-3">Related Articles</h3>
                <div className="space-y-3">
                  {relatedPosts.map(related => (
                    <Link
                      key={related.id}
                      to={`/blog/${related.id}`}
                      className="flex gap-3 hover:bg-white p-2 rounded-lg transition group"
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xs font-semibold text-black group-hover:text-primary transition line-clamp-2">
                          {related.title}
                        </h4>
                        <p className="text-[10px] text-gray-500 mt-1">{related.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Newsletter */}
            <div className="bg-gradient-to-r from-primary/10 to-orange-500/10 rounded-xl p-5 mt-6">
              <h3 className="text-sm font-bold text-black mb-2">Subscribe to Newsletter</h3>
              <p className="text-xs text-gray-600 mb-3">Get the latest articles straight to your inbox.</p>
              <input
                type="email"
                placeholder="Your email"
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm mb-2 focus:outline-none focus:border-primary"
              />
              <button className="w-full py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .prose h2 {
          font-size: 1.25rem;
          font-weight: 700;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: #1a1a1a;
        }
        .prose p {
          margin-bottom: 1rem;
        }
      `}</style>
    </main>
  );
};

export default BlogDetailPage;